import type { i18n } from 'i18next';
import type { ReactNode } from 'react';
import { createInstance } from 'i18next';
import { useContext, useEffect, useRef } from 'react';
import { I18nContext, I18nextProvider } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
// eslint-disable-next-line import/extensions
import './types/i18next.d.ts';

// zod
import * as z from 'zod'; 
import {
  fr,
  // es,
} from 'zod/locales';
import validations from '@/locales/validations.json';

import { resources } from '@/locales/resources';

// Utils
import {
  buildLanguageSwitchUrl,
  buildLocalizedPath,
  getLocale,
  getSlug,
  isCrossDomainLanguageSwitch,
  localizePath,
} from '@/utils/localeSlug';
import { resolveContactSlug } from '@/utils/contactSlug';

const configureZodLocale = (language: string) => {
  const zodLocaleMap = {
    fr,
    // es,
  };
  
  const zodLocale = zodLocaleMap[language as keyof typeof zodLocaleMap] || fr;
  const messages = validations[language as keyof typeof validations] || validations.fr;
  
  z.config({
    ...zodLocale(),
    customError: (iss) => {
      if (iss.code === 'too_small' && iss.origin === 'string') {
        return messages.string_required;
      }
  
      if (iss.code === 'invalid_value' && iss.input === false) {
        return messages.checkbox_required;
      }

      if (iss.code === 'invalid_format' && iss.format === 'email') {
        return messages.invalid_email;
      }
      
      return undefined;
    },
  });
};

export function I18n({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const currentLng = getLocale(pathname);
  const i18nRef = useRef<null | i18n>(null);
  
  if (!i18nRef.current) {
    i18nRef.current = createInstance({ lng: currentLng, defaultNS: 'translation', ns: ['translation'], resources });
    i18nRef.current.init();
    configureZodLocale(currentLng.split('-')[0]);
  }

  return (
    <I18nextProvider i18n={i18nRef.current}>
      {children}
    </I18nextProvider>
  );
}

const lngs = Object.keys(resources);

export function useSyncLng() {
  const { pathname } = useLocation();
  const currentLng = getLocale(pathname);
  const context = useContext(I18nContext);

  const nav = useNavigate();
  useEffect(() => {
    if (!lngs.includes(currentLng)) {
      nav(buildLocalizedPath('', 'fr-FR'));
      return;
    }
    if (currentLng !== context.i18n.language) {
      context.i18n.changeLanguage(currentLng);
    }
  }, [nav, currentLng, context.i18n]);
}

export function useI18n() {
  const nav = useNavigate();
  const { pathname } = useLocation();

  function getLng(): keyof typeof resources {
    return getLocale(pathname);
  }

  const localizedPath = (url: string) => localizePath(url, getLng());
  
  const changeLng = (lng: keyof typeof resources) => {
    const slug = resolveContactSlug(getSlug(pathname), lng);
    const path = buildLocalizedPath(slug, lng);

    if (isCrossDomainLanguageSwitch(window.location.hostname, lng)) {
      window.location.assign(buildLanguageSwitchUrl({
        slug,
        locale: lng,
        hostname: window.location.hostname,
        protocol: window.location.protocol,
      }));
      return;
    }

    nav(path, { replace: true });
  };

  const context = useContext(I18nContext);

  return {
    getLng,
    localizedPath,
    changeLng,
    i18n: context?.i18n,
  };
}